// Orden de Compra (OCC) · docs/108 B6. Una pestaña por OC: sin id es una OC NUEVA (editor); con id,
// la ficha con su ciclo de vida (borrador → aprobación → emitida → recibida/cerrada), lo recibido
// DERIVADO de las recepciones, las recepciones de la OC y su historia. Las cifras las calcula el
// backend; aquí solo se muestran (la estimación del editor es referencial y lo dice).
import { useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Imprimir, EncabezadoImpreso } from '../comun/Imprimir';
import { decimal, entradaDecimal, fecha, pesos, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { SelectorBodega, useBodegas } from '../stock/PantallasStock';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import { BuscadorProducto } from '../ventas/BuscadorProducto';
import { DialogoMotivo, estimar, Historia } from './comun';
import {
  AvisoDoc,
  CaminoDoc,
  CuerpoDoc,
  DatosDoc,
  EncabezadoDoc,
  Tarjeta,
  type Paso,
} from './FichaDocumento';
import {
  CONDICIONES_PAGO,
  NOMBRE_CUMPLIMIENTO,
  NOMBRE_ESTADO_OC,
  NOMBRE_ESTADO_RECEPCION,
  useAccionCompras,
  useOrdenCompra,
  useRecepciones,
  type CompraDeProducto,
  type OrdenLeida,
} from './datos';

interface LineaEditor {
  clave: string;
  productoId: string;
  presentacionId: string;
  sku: string;
  nombre: string;
  presentacion: string;
  cantidad: string;
  precio: string;
  descuento: string;
}

interface Proveedor {
  id: string;
  razonSocial: string;
}

export function OrdenCompra({
  ordenId,
  alCreada,
  alRecibir,
  alAbrirRecepcion,
}: {
  ordenId?: string | undefined;
  alCreada: (ordenId: string, numero: string) => void;
  alRecibir: (ordenId: string, numero: string) => void;
  alAbrirRecepcion: (recepcionId: string, numero: string) => void;
}) {
  const { empresa } = useSesion();
  const q = useOrdenCompra(empresa!.id, ordenId ?? null);
  const [editando, setEditando] = useState(false);
  if (!ordenId) return <EditorOc alGuardada={alCreada} />;
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <p className="apagado pantalla">Cargando OC…</p>;
  if (editando)
    return (
      <EditorOc
        orden={q.data}
        alGuardada={() => setEditando(false)}
        alCancelar={() => setEditando(false)}
      />
    );
  return (
    <FichaOc
      o={q.data}
      alEditar={() => setEditando(true)}
      alRecibir={alRecibir}
      alAbrirRecepcion={alAbrirRecepcion}
    />
  );
}

// ================================================================ editor (nueva o borrador)

function EditorOc({
  orden,
  alGuardada,
  alCancelar,
}: {
  orden?: OrdenLeida;
  alGuardada: (ordenId: string, numero: string) => void;
  alCancelar?: () => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const accion = useAccionCompras(empresaId);
  const o = orden?.orden;
  const [proveedor, setProveedor] = useState<Proveedor | null>(
    o ? { id: o.proveedor_entidad_id, razonSocial: o.proveedor_razon_social } : null,
  );
  const [bodega, setBodega] = useState(o?.ubicacion_destino_id ?? '');
  const [entrega, setEntrega] = useState(o?.fecha_entrega_esperada?.slice(0, 10) ?? '');
  const [condicion, setCondicion] = useState(o?.condicion_pago ?? '');
  const [plazo, setPlazo] = useState(o ? String(o.plazo_dias) : '');
  const [observaciones, setObservaciones] = useState(o?.observaciones ?? '');
  const [lineas, setLineas] = useState<LineaEditor[]>(
    (orden?.lineas ?? []).map((l) => ({
      clave: l.id,
      productoId: l.productoId,
      presentacionId: l.presentacionId,
      sku: l.sku,
      nombre: l.descripcion,
      presentacion: l.presentacionCodigo,
      cantidad: decimal(l.cantidad).replace(/\./g, ''),
      precio: decimal(l.precioUnitario).replace(/\./g, ''),
      descuento: Number(l.descuento) ? l.descuento : '',
    })),
  );
  const [buscandoProv, setBuscandoProv] = useState(false);
  const [buscandoProd, setBuscandoProd] = useState(false);
  const bodegaEfectiva = bodega || bodegas.lista[0]?.id || '';

  const cambiar = (clave: string, campo: keyof LineaEditor, valor: string) =>
    setLineas((ls) => ls.map((l) => (l.clave === clave ? { ...l, [campo]: valor } : l)));

  const lineasApi = lineas.map((l) => ({
    productoId: l.productoId,
    presentacionId: l.presentacionId,
    cantidad: entradaDecimal(l.cantidad, 6),
    precioUnitario: entradaDecimal(l.precio, 4),
    ...(l.descuento.trim() ? { descuento: entradaDecimal(l.descuento, 0) } : {}),
  }));
  const invalidas = lineasApi.filter(
    (l) => !l.cantidad || l.cantidad === '0' || !l.precioUnitario || l.descuento === null,
  ).length;
  const estimado = lineas.reduce(
    (s, l) =>
      s + Math.round(estimar(l.cantidad) * estimar(l.precio)) - Math.round(estimar(l.descuento)),
    0,
  );
  const puedeGuardar =
    !!proveedor && !!bodegaEfectiva && lineas.length > 0 && invalidas === 0 && !accion.isPending;

  const guardar = () => {
    const comun = {
      proveedorEntidadId: proveedor!.id,
      ubicacionDestinoId: bodegaEfectiva,
      ...(condicion ? { condicionPago: condicion } : {}),
      ...(plazo.trim() && /^\d+$/.test(plazo.trim()) ? { plazoDias: Number(plazo.trim()) } : {}),
      lineas: lineasApi,
    };
    if (o)
      accion.mutate(
        {
          ruta: `/compras/ordenes/${o.id}`,
          metodo: 'PATCH',
          cuerpo: {
            version: o.version,
            ...comun,
            fechaEntregaEsperada: entrega || null,
            observaciones: observaciones.trim() || null,
          },
        },
        { onSuccess: () => alGuardada(o.id, orden!.numeroVisible) },
      );
    else
      accion.mutate(
        {
          ruta: '/compras/ordenes',
          cuerpo: {
            empresaId,
            ...comun,
            ...(entrega ? { fechaEntregaEsperada: entrega } : {}),
            ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
          },
        },
        {
          onSuccess: (r) => {
            const x = r as { ordenId: string; numeroVisible: string };
            alGuardada(x.ordenId, x.numeroVisible);
          },
        },
      );
  };

  /** Precio sugerido: el último costo de compra del producto en esa presentación. */
  const sugerirPrecio = async (productoId: string, factor: string): Promise<string> => {
    try {
      const h = await api.get<CompraDeProducto[]>('/compras/costos', {
        query: { empresaId, productoId },
      });
      const u = h[0];
      if (!u) return '';
      const v = Math.round(Number(u.costoBase) * Number(factor) * 100) / 100;
      return String(v).replace('.', ',');
    } catch {
      return '';
    }
  };

  return (
    <div className="pantalla documento-compra" data-pantalla="oc-editor">
      <div className="encabezado-entidad">
        <span className="ruta-migas">Compras › Orden de compra</span>
        <h2>{o ? `Editar ${orden!.numeroVisible}` : 'Nueva orden de compra'}</h2>
      </div>
      <div className="barra-filtros">
        <div className="campo">
          <span className="etiqueta">Proveedor</span>
          <button
            type="button"
            className="selector-cliente"
            aria-label="Elegir proveedor"
            onClick={() => setBuscandoProv(true)}
          >
            {proveedor ? proveedor.razonSocial : 'Buscar proveedor…'}
          </button>
        </div>
        <SelectorBodega bodegas={bodegas.lista} valor={bodegaEfectiva} alCambiar={setBodega} />
        <label className="campo">
          <span className="etiqueta">Entrega esperada</span>
          <input
            type="date"
            aria-label="Entrega esperada"
            value={entrega}
            onChange={(e) => setEntrega(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Condición de pago</span>
          <select
            aria-label="Condición de pago"
            value={condicion}
            onChange={(e) => setCondicion(e.target.value)}
          >
            <option value="">La del proveedor</option>
            {CONDICIONES_PAGO.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="campo campo-corto">
          <span className="etiqueta">Plazo (días)</span>
          <input
            aria-label="Plazo en días"
            inputMode="numeric"
            value={plazo}
            onChange={(e) => setPlazo(e.target.value)}
          />
        </label>
      </div>
      <table className="grilla compacta" aria-label="Líneas de la orden de compra">
        <thead>
          <tr>
            <th style={{ width: '3rem' }}>N°</th>
            <th style={{ width: '8rem' }}>SKU</th>
            <th>Producto</th>
            <th style={{ width: '6rem' }}>Present.</th>
            <th style={{ width: '7rem' }} className="der">
              Cantidad
            </th>
            <th style={{ width: '8rem' }} className="der">
              Precio neto
            </th>
            <th style={{ width: '7rem' }} className="der">
              Descuento $
            </th>
            <th style={{ width: '8rem' }} className="der">
              Neto (est.)
            </th>
            <th style={{ width: '3rem' }} />
          </tr>
        </thead>
        <tbody>
          {lineas.map((l, i) => (
            <tr key={l.clave}>
              <td>{i + 1}</td>
              <td className="mono">{l.sku}</td>
              <td>{l.nombre}</td>
              <td>{l.presentacion}</td>
              <td className="der">
                <input
                  className="entrada-numero"
                  aria-label={`Cantidad línea ${i + 1}`}
                  value={l.cantidad}
                  onChange={(e) => cambiar(l.clave, 'cantidad', e.target.value)}
                />
              </td>
              <td className="der">
                <input
                  className="entrada-numero"
                  aria-label={`Precio línea ${i + 1}`}
                  value={l.precio}
                  onChange={(e) => cambiar(l.clave, 'precio', e.target.value)}
                />
              </td>
              <td className="der">
                <input
                  className="entrada-numero"
                  aria-label={`Descuento línea ${i + 1}`}
                  value={l.descuento}
                  onChange={(e) => cambiar(l.clave, 'descuento', e.target.value)}
                />
              </td>
              <td className="der">
                {pesos(
                  String(
                    Math.round(estimar(l.cantidad) * estimar(l.precio)) -
                      Math.round(estimar(l.descuento)),
                  ),
                )}
              </td>
              <td>
                <button
                  type="button"
                  className="boton-mini"
                  aria-label={`Quitar línea ${i + 1}`}
                  onClick={() => setLineas((ls) => ls.filter((x) => x.clave !== l.clave))}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
          {!lineas.length && (
            <tr>
              <td colSpan={9} className="grilla-vacia">
                Agrega productos con «Agregar producto» (F3).
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="barra-filtros">
        <button type="button" onClick={() => setBuscandoProd(true)}>
          Agregar producto
        </button>
        <span className="espaciador" />
        <span>
          Neto estimado <b>{pesos(String(estimado))}</b>{' '}
          <span className="apagado">
            (los impuestos y el total los calcula el sistema al guardar)
          </span>
        </span>
      </div>
      <label className="campo">
        <span className="etiqueta">Observaciones</span>
        <textarea
          aria-label="Observaciones"
          rows={2}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </label>
      {invalidas > 0 && (
        <p className="tono-malo">
          Hay {invalidas} línea(s) con cantidad o precio inválido (cantidad hasta 6 decimales,
          precio hasta 4, descuento en pesos enteros).
        </p>
      )}
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-od">
        {alCancelar && (
          <button type="button" onClick={alCancelar}>
            Volver sin guardar
          </button>
        )}
        <button type="button" className="primario" disabled={!puedeGuardar} onClick={guardar}>
          {o ? 'Guardar cambios' : 'Crear OC (borrador)'}
        </button>
      </div>
      {buscandoProv && (
        <BuscadorCliente
          empresaId={empresaId}
          rol="proveedor"
          alElegir={(c) => {
            setProveedor({ id: c.id, razonSocial: c.razon_social });
            if (!condicion && c.condicion_pago) setCondicion(c.condicion_pago);
            if (!plazo && c.plazo_dias !== null) setPlazo(String(c.plazo_dias));
            setBuscandoProv(false);
          }}
          alCerrar={() => setBuscandoProv(false)}
        />
      )}
      {buscandoProd && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={bodegaEfectiva}
          ubicaciones={bodegas.lista}
          alElegir={(p) => {
            setBuscandoProd(false);
            const clave = `${p.producto.id}:${Date.now()}`;
            setLineas((ls) => [
              ...ls,
              {
                clave,
                productoId: p.producto.id,
                presentacionId: p.presentacion.id,
                sku: p.producto.sku,
                nombre: p.producto.nombre,
                presentacion: p.presentacion.codigo,
                cantidad: '1',
                precio: '',
                descuento: '',
              },
            ]);
            void sugerirPrecio(p.producto.id, p.presentacion.factor_a_base).then((precio) => {
              if (precio)
                setLineas((ls) =>
                  ls.map((l) => (l.clave === clave && !l.precio ? { ...l, precio } : l)),
                );
            });
          }}
          alCerrar={() => setBuscandoProd(false)}
        />
      )}
    </div>
  );
}

// ================================================================ ficha

type AccionOc =
  | 'enviar-aprobacion'
  | 'retirar-aprobacion'
  | 'aprobar'
  | 'rechazar'
  | 'reabrir'
  | 'emitir'
  | 'cancelar'
  | 'cerrar-corta';

const CON_MOTIVO: Partial<Record<AccionOc, { titulo: string; boton: string; texto: string }>> = {
  aprobar: { titulo: 'Aprobar OC', boton: 'Aprobar', texto: 'La aprobación firma este contenido.' },
  rechazar: { titulo: 'Rechazar OC', boton: 'Rechazar', texto: 'La OC vuelve a borrador.' },
  'retirar-aprobacion': {
    titulo: 'Retirar de aprobación',
    boton: 'Retirar',
    texto: 'La OC vuelve a borrador para corregirla.',
  },
  reabrir: {
    titulo: 'Reabrir OC',
    boton: 'Reabrir',
    texto: 'Vuelve a borrador; se aprueba de nuevo.',
  },
  cancelar: {
    titulo: 'Cancelar OC',
    boton: 'Cancelar OC',
    texto: 'Una OC emitida con recepciones no se cancela: se cierra corta.',
  },
  'cerrar-corta': {
    titulo: 'Cerrar corta',
    boton: 'Cerrar corta',
    texto: 'Lo recibido queda; la OC deja de esperar el resto.',
  },
};

function FichaOc({
  o,
  alEditar,
  alRecibir,
  alAbrirRecepcion,
}: {
  o: OrdenLeida;
  alEditar: () => void;
  alRecibir: (ordenId: string, numero: string) => void;
  alAbrirRecepcion: (recepcionId: string, numero: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const recepciones = useRecepciones(empresaId, { ordenId: o.orden.id });
  const accion = useAccionCompras(empresaId);
  const [dialogo, setDialogo] = useState<AccionOc | null>(null);
  const [imprimir, setImprimir] = useState(false);
  const e = o.orden.estado;
  const recibida = new Map(o.cumplimiento.lineas.map((l) => [l.numero, l]));
  const bodega = bodegas.lista.find((b) => b.id === o.orden.ubicacion_destino_id);

  const ejecutar = (a: AccionOc, motivo?: string) =>
    accion.mutate(
      {
        ruta: `/compras/ordenes/${o.orden.id}/${a}`,
        cuerpo: { version: o.orden.version, ...(motivo ? { motivo } : {}) },
      },
      { onSuccess: () => setDialogo(null) },
    );
  const pedir = (a: AccionOc) => (CON_MOTIVO[a] ? setDialogo(a) : ejecutar(a));

  const botones: Array<{ a: AccionOc; texto: string; permiso: string; primario?: boolean }> = [];
  if (e === 'BORRADOR')
    botones.push(
      {
        a: 'enviar-aprobacion',
        texto: 'Enviar a aprobación',
        permiso: 'compras.oc.enviar_aprobacion',
        primario: true,
      },
      { a: 'cancelar', texto: 'Cancelar OC', permiso: 'compras.oc.cancelar' },
    );
  if (e === 'EN_APROBACION')
    botones.push(
      { a: 'aprobar', texto: 'Aprobar', permiso: 'compras.oc.aprobar', primario: true },
      { a: 'rechazar', texto: 'Rechazar', permiso: 'compras.oc.rechazar' },
      {
        a: 'retirar-aprobacion',
        texto: 'Retirar de aprobación',
        permiso: 'compras.oc.enviar_aprobacion',
      },
      { a: 'cancelar', texto: 'Cancelar OC', permiso: 'compras.oc.cancelar' },
    );
  if (e === 'APROBADA')
    botones.push(
      { a: 'emitir', texto: 'Emitir al proveedor', permiso: 'compras.oc.emitir', primario: true },
      { a: 'reabrir', texto: 'Reabrir', permiso: 'compras.oc.editar' },
      { a: 'cancelar', texto: 'Cancelar OC', permiso: 'compras.oc.cancelar' },
    );
  if (e === 'EMITIDA')
    botones.push(
      { a: 'cerrar-corta', texto: 'Cerrar corta', permiso: 'compras.oc.cerrar_corta' },
      { a: 'cancelar', texto: 'Cancelar OC', permiso: 'compras.oc.cancelar' },
    );

  const cumple = o.cumplimiento.estado;
  const pasos: Paso[] = [
    {
      texto: 'Borrador',
      estado: e === 'BORRADOR' ? 'actual' : 'hecho',
      ayuda: fecha(o.orden.fecha_negocio),
    },
    {
      texto: 'Aprobada',
      estado:
        e === 'EN_APROBACION'
          ? 'actual'
          : ['APROBADA', 'EMITIDA', 'CERRADA'].includes(e)
            ? 'hecho'
            : 'pendiente',
      ayuda: e === 'EN_APROBACION' ? 'esperando la firma' : undefined,
    },
    {
      texto: 'Emitida al proveedor',
      estado:
        e === 'APROBADA' ? 'actual' : ['EMITIDA', 'CERRADA'].includes(e) ? 'hecho' : 'pendiente',
    },
    {
      texto: 'Recibida',
      estado:
        cumple === 'COMPLETA' || cumple === 'SOBRE_RECIBIDA' || e === 'CERRADA'
          ? 'hecho'
          : cumple === 'PARCIAL'
            ? 'aviso'
            : e === 'EMITIDA'
              ? 'actual'
              : 'pendiente',
      ayuda: e === 'EMITIDA' || e === 'CERRADA' ? NOMBRE_CUMPLIMIENTO[cumple] : undefined,
    },
  ];
  const acciones = (
    <>
      {e === 'BORRADOR' && puede('compras.oc.editar') && (
        <button type="button" onClick={alEditar}>
          Editar
        </button>
      )}
      {botones
        .filter((b) => puede(b.permiso))
        .map((b) => (
          <button
            key={b.a}
            type="button"
            className={b.primario ? 'primario' : undefined}
            disabled={accion.isPending}
            onClick={() => pedir(b.a)}
          >
            {b.texto}
          </button>
        ))}
      {e === 'EMITIDA' && puede('compras.recepcion.crear') && (
        <button
          type="button"
          className="primario"
          onClick={() => alRecibir(o.orden.id, o.numeroVisible)}
        >
          Recibir mercadería
        </button>
      )}
      <button type="button" onClick={() => setImprimir(true)}>
        Imprimir OC
      </button>
    </>
  );

  return (
    <div className="pantalla documento-compra doc-ficha" data-pantalla="oc-ficha">
      <EncabezadoDoc
        sigla="OCC"
        migas="Compras › Orden de compra"
        titulo={o.numeroVisible}
        insignias={
          <span
            className={`insignia ${e === 'CANCELADA' ? 'tono-malo' : e === 'EMITIDA' || e === 'CERRADA' ? 'tono-ok' : 'tono-info'}`}
            data-estado={e}
          >
            {NOMBRE_ESTADO_OC[e]}
          </span>
        }
        sub={
          <>
            <b>{o.orden.proveedor_razon_social}</b> · RUT{' '}
            <span className="mono">{rut(o.orden.proveedor_rut)}</span> · entrega{' '}
            {fecha(o.orden.fecha_entrega_esperada)}
          </>
        }
        acciones={acciones}
      />
      {e === 'CANCELADA' ? (
        <AvisoDoc tono="malo">
          <b>OC cancelada.</b>
        </AvisoDoc>
      ) : (
        <CaminoDoc pasos={pasos} />
      )}
      {o.orden.observaciones && (
        <AvisoDoc tono="info">
          <b>Observaciones:</b> {o.orden.observaciones}
        </AvisoDoc>
      )}
      {accion.isError && !dialogo && (
        <div className="doc-aviso tono-malo">
          <PanelError error={accion.error} />
        </div>
      )}
      <CuerpoDoc
        principal={
          <>
            <Tarjeta titulo="Productos pedidos" extra={`${o.lineas.length} líneas`}>
              <table className="grilla compacta" aria-label="Líneas de la OC">
                <thead>
                  <tr>
                    <th style={{ width: '3rem' }}>N°</th>
                    <th style={{ width: '8rem' }}>SKU</th>
                    <th>Descripción</th>
                    <th style={{ width: '6rem' }}>Present.</th>
                    <th className="der">Cantidad</th>
                    <th className="der">Precio</th>
                    <th className="der">Neto</th>
                    <th className="der">Recibido (base)</th>
                  </tr>
                </thead>
                <tbody>
                  {o.lineas.map((l) => {
                    const c = recibida.get(l.numero);
                    return (
                      <tr key={l.id}>
                        <td>{l.numero}</td>
                        <td className="mono">{l.sku}</td>
                        <td>{l.descripcion}</td>
                        <td>{l.presentacionCodigo}</td>
                        <td className="der">{decimal(l.cantidad)}</td>
                        <td className="der">{pesos(l.precioUnitario)}</td>
                        <td className="der">{pesos(l.neto)}</td>
                        <td className="der">
                          {c ? `${decimal(c.recibidaBase)} / ${decimal(c.ordenadaBase)}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {o.totales.impuestos.map((i) => (
                    <tr key={i.codigo}>
                      <td colSpan={6} className="der apagado">
                        {i.codigo}
                      </td>
                      <td className="der">{pesos(i.monto)}</td>
                      <td />
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={6} className="der">
                      <b>Total</b>
                    </td>
                    <td className="der">
                      <b>{pesos(o.totales.total)}</b>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </Tarjeta>
            <Tarjeta titulo="Guías de recepción de esta OC" etiqueta="Recepciones">
              <table className="grilla compacta" aria-label="Recepciones de la OC">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th>Fecha</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {(recepciones.data?.items ?? []).map((r) => (
                    <tr
                      key={r.id}
                      className="fila-enlace"
                      onDoubleClick={() => alAbrirRecepcion(r.id, r.numero_visible ?? 'Recepción')}
                    >
                      <td>
                        <button
                          type="button"
                          className="enlace"
                          onClick={() => alAbrirRecepcion(r.id, r.numero_visible ?? 'Recepción')}
                        >
                          {r.numero_visible ?? 'borrador'}
                        </button>
                      </td>
                      <td>{fecha(r.fecha_negocio)}</td>
                      <td>{NOMBRE_ESTADO_RECEPCION[r.estado]}</td>
                    </tr>
                  ))}
                  {!recepciones.data?.items.length && (
                    <tr>
                      <td colSpan={3} className="grilla-vacia">
                        Sin recepciones
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Tarjeta>
            <Tarjeta titulo="Historia" etiqueta="Historia">
              <Historia transiciones={o.transiciones} />
            </Tarjeta>
          </>
        }
        lateral={
          <>
            <Tarjeta titulo="Datos de la OC" etiqueta="Datos de la OC">
              <DatosDoc
                datos={[
                  { e: 'Proveedor', v: o.orden.proveedor_razon_social },
                  { e: 'RUT', v: rut(o.orden.proveedor_rut), mono: true },
                  { e: 'Fecha', v: fecha(o.orden.fecha_negocio) },
                  { e: 'Entrega', v: fecha(o.orden.fecha_entrega_esperada) },
                  { e: 'Bodega destino', v: bodega ? `${bodega.codigo} · ${bodega.nombre}` : '…' },
                  { e: 'Condición', v: `${o.orden.condicion_pago} · ${o.orden.plazo_dias} días` },
                ]}
              />
            </Tarjeta>
            <Tarjeta titulo="Totales" etiqueta="Totales de la OC">
              <DatosDoc
                datos={[
                  { e: 'Neto', v: pesos(o.totales.neto) },
                  ...o.totales.impuestos.map((i) => ({
                    e: i.codigo.replace(/_/g, ' '),
                    v: pesos(i.monto),
                  })),
                  { e: 'Total', v: pesos(o.totales.total), fuerte: true },
                ]}
              />
            </Tarjeta>
          </>
        }
      />
      {dialogo && CON_MOTIVO[dialogo] && (
        <DialogoMotivo
          titulo={`${CON_MOTIVO[dialogo]!.titulo} ${o.numeroVisible}`}
          explicacion={CON_MOTIVO[dialogo]!.texto}
          textoBoton={CON_MOTIVO[dialogo]!.boton}
          pendiente={accion.isPending}
          error={accion.isError ? accion.error : null}
          alConfirmar={(m) => ejecutar(dialogo, m)}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {imprimir && (
        <Imprimir alTerminar={() => setImprimir(false)}>
          <OcImpresa o={o} empresa={empresa!.razon_social} bodega={bodega?.nombre ?? ''} />
        </Imprimir>
      )}
    </div>
  );
}

/** La OC en papel (o PDF) para enviar al proveedor. */
export function OcImpresa({
  o,
  empresa,
  bodega,
}: {
  o: OrdenLeida;
  empresa: string;
  bodega: string;
}) {
  return (
    <article className="impreso">
      <EncabezadoImpreso
        empresa={empresa}
        titulo="Orden de compra"
        numero={o.numeroVisible}
        detalle={
          <dl className="impreso-datos">
            <dt>Proveedor</dt>
            <dd>
              {o.orden.proveedor_razon_social} · {rut(o.orden.proveedor_rut)}
            </dd>
            <dt>Fecha</dt>
            <dd>{fecha(o.orden.fecha_negocio)}</dd>
            <dt>Entregar en</dt>
            <dd>
              {bodega}
              {o.orden.fecha_entrega_esperada ? ` · ${fecha(o.orden.fecha_entrega_esperada)}` : ''}
            </dd>
            <dt>Condición</dt>
            <dd>
              {o.orden.condicion_pago} · {o.orden.plazo_dias} días
            </dd>
          </dl>
        }
      />
      <table className="impreso-tabla">
        <thead>
          <tr>
            <th>N°</th>
            <th>Código</th>
            <th>Descripción</th>
            <th>Unidad</th>
            <th className="der">Cantidad</th>
            <th className="der">Precio</th>
            <th className="der">Neto</th>
          </tr>
        </thead>
        <tbody>
          {o.lineas.map((l) => (
            <tr key={l.id}>
              <td>{l.numero}</td>
              <td>{l.sku}</td>
              <td>{l.descripcion}</td>
              <td>{l.presentacionCodigo}</td>
              <td className="der">{decimal(l.cantidad)}</td>
              <td className="der">{pesos(l.precioUnitario)}</td>
              <td className="der">{pesos(l.neto)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6} className="der">
              Neto
            </td>
            <td className="der">{pesos(o.totales.neto)}</td>
          </tr>
          {o.totales.impuestos.map((i) => (
            <tr key={i.codigo}>
              <td colSpan={6} className="der">
                {i.codigo}
              </td>
              <td className="der">{pesos(i.monto)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={6} className="der">
              <b>Total</b>
            </td>
            <td className="der">
              <b>{pesos(o.totales.total)}</b>
            </td>
          </tr>
        </tfoot>
      </table>
      {o.orden.observaciones && <p>Observaciones: {o.orden.observaciones}</p>}
    </article>
  );
}
